/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { limitedOperators } from 'cio/utils/conditions-operators';
import CampaignModel from 'cio/models/campaign';
import { ConditionEditorSubLayout } from 'cio/utils/condition-editor';
import convertOperator from 'cio/utils/operators/convert';
import ObjectAttributeCondition, { ObjectAttributeSource } from 'cio/app-models/conditions/object-attribute';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { action } from '@ember/object';
import DS from 'ember-data';
import ObjectTypesService from 'cio/services/object-types';

interface EditorArgs {
  condition?: ObjectAttributeCondition;
  campaign?: CampaignModel;
  disabled?: boolean;
  attributes: Record<string, any>;
  variation?: string;
  subLayout?: ConditionEditorSubLayout;
  direction?: string;
  size?: string;
  gapSize?: string;
  dropdownClass?: string;
  marginClass?: string;
  allowComparisonOperator?: boolean;
  allowArrayOperators?: boolean;
  allowedConditionOperators?: string[] | null;
  autosuggestValueOverrides?: Record<string, string[]>;
  isLoadingAutosuggestValueOverrides?: boolean;
  showEventNameInEditor?: boolean;
  onConditionChange?: () => unknown;
  allowJsonOperators?: boolean;
  isJsonChild?: boolean;
  objectTypeId?: string;
}

export default class Editor extends Component<EditorArgs> {
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service objectTypes!: ObjectTypesService;

  get showObjectTypeSelector() {
    return this.args.objectTypeId == null;
  }

  get objectTypesOptions() {
    return this.objectTypes.enabledObjectTypes.map((ot) => ({ value: ot.id, label: ot.name }));
  }

  get limitedOperators() {
    let operators: string[] = limitedOperators.map((operator) => convertOperator(operator.value));

    if (this.args.allowedConditionOperators != null) {
      operators.push(...this.args.allowedConditionOperators);
    }

    return operators.uniq();
  }

  get showEventNameInEditor() {
    return this.args.showEventNameInEditor ?? true;
  }

  get allowJsonOperators() {
    return this.args.allowJsonOperators ?? false;
  }

  get isJsonChild() {
    return this.args.condition?.isJsonChild ?? false;
  }

  get placeholder() {
    if (this.args.condition?.objectAttributeSource === ObjectAttributeSource.RelationshipAttribute) {
      return 'relationship attribute';
    }

    let name = 'object';
    if (this.args.objectTypeId != null) {
      name = this.store.peekRecord('object-type', this.args.objectTypeId)?.name.toLowerCase() ?? name;
    }

    return `${name} attribute`;
  }

  get objectTypeId() {
    return this.args.objectTypeId ?? this.args.condition?.objectTypeId;
  }

  @action
  onObjectTypeChange() {
    let condition = this.args.condition?.editableCondition ?? this.args.condition;
    condition?.applyUpdates();
  }
}
